<template>
    <div id="tour-student-timetable-page" class="flex flex-col lg:flex-row flex-1 space-y-2 lg:space-y-0 lg:space-x-2 h-full">
        <main class="overflow-y-scroll h-screen flex-1">
            <div class="flex-1 bg-white p-6 rounded relative">

                <div class="sticky top-[50px] bg-white z-10 mt-4 ">

                    <div class=" overflow-y-auto h-[calc(100vh-100px)]">

                        <div v-if="loadingTimetable" class="flex justify-center py-20">
                            <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
                        </div>

                        <div v-else-if="!currentClassroomSection" class="text-center py-10 text-gray-500">
                            You are not currently assigned to a {{ labels.CLASSROOM_SECTION.toLowerCase() }}.
                        </div>

                        <div v-else-if="!timetable" class="text-center py-10 text-gray-500">
                            No timetable found for your class.
                        </div>

                        <div v-else class="mt-4">
                            <h2 class="text-lg font-bold mb-4 text-center sticky left-0">
                                Timetable
                            </h2>
                            <TimetableGrid :slots="sortedSlots" :entries="timetable.entries" :days="days"
                                :editable="false" @cell-click="handleCellClick" />
                        </div>

                        <!-- Details Dialog -->
                        <Dialog v-model:visible="showDetails" header="Timetable Details" :modal="true" class="w-96 rounded-xl">
                            <div v-if="selectedEntry" class="flex flex-col gap-4">
                                <div class="bg-gray-50 p-4 rounded-lg">
                                    <span class="block text-xs uppercase text-gray-500 font-bold mb-1">{{ labels.SUBJECT }}</span>
                                    <span class="block text-xl font-bold text-gray-800">{{ teachableName(selectedEntry) ||
                                        selectedEntry.custom_title || `No ${labels.SUBJECT}` }}</span>
                                </div>
                                <div class="grid grid-cols-2 gap-4">
                                    <div class="bg-gray-50 p-3 rounded-lg">
                                        <span class="block text-xs uppercase text-gray-500 font-bold mb-1">Time</span>
                                        <span class="text-sm font-medium">{{ formatTime(selectedEntry.timeSlot?.start_time) }} -
                                            {{ formatTime(selectedEntry.timeSlot?.end_time) }}</span>
                                    </div>
                                    <div class="bg-gray-50 p-3 rounded-lg">
                                        <span class="block text-xs uppercase text-gray-500 font-bold mb-1">Day</span>
                                        <span class="text-sm font-medium">{{ selectedEntry.day_of_week }}</span>
                                    </div>
                                </div>
                                <div class="bg-gray-50 p-3 rounded-lg">
                                    <span class="block text-xs uppercase text-gray-500 font-bold mb-1">{{ labels.TEACHER }}</span>
                                    <div class="flex items-center gap-2">
                                        <div class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                                             {{ (selectedEntry.staff?.user?.name?.charAt(0) || 'T').toUpperCase() }}
                                        </div>
                                        <span class="text-sm font-medium">{{ selectedEntry.staff?.user?.name || `No ${labels.TEACHER} Assigned` }}</span>
                                    </div>
                                </div>
                                <div v-if="selectedEntry.room_name" class="bg-gray-50 p-3 rounded-lg">
                                    <span class="block text-xs uppercase text-gray-500 font-bold mb-1">Location</span>
                                    <div class="flex items-center gap-2 text-gray-700">
                                        <i class="pi pi-map-marker"></i>
                                        <span class="text-sm font-medium">{{ selectedEntry.room_name }}</span>
                                    </div>
                                </div>
                            </div>
                            <template #footer>
                                <div class="flex justify-end">
                                    <Button label="Close" class="p-button-text" @click="showDetails = false" />
                                </div>
                            </template>
                        </Dialog>

                    </div>

                </div>

            </div>

        </main>

    </div>

</template>

<script setup lang="ts">
import { useGetTimetablesQuery } from '@/types/vue-apollo-helpers';
import TimetableGrid from '@/components/DashBoard/Timetable/TimetableGrid.vue';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import { TimetableTimeableType, type TimetableEntry } from '@/types/composition-functions';
import { teachableName } from '@/utils/teachable';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels: labelsRef } = useSchoolLabels();
const labels = computed(() => labelsRef.value);

// Sidebar toggle
const toggleSidebar = inject<() => void>('toggleSidebar', () => { });

// Data Fetching
const { currentClassroomSection } = useUserQuery();

const { result: timetableResult, loading: loadingTimetable } = useGetTimetablesQuery(
  () => ({
    timeable_id: currentClassroomSection.value?.id,
    timeable_type: TimetableTimeableType.ClassroomSection,
  }),
  {
    enabled: computed(() => !!currentClassroomSection.value?.id),
  },
);

const timetable = computed(() => timetableResult.value?.timetables?.data?.[0] || null);

const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const sortedSlots = computed(() => {
  if (!timetable.value?.template?.timeSlots) return [];
  return [...timetable.value.template.timeSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
});

// Details Modal
const showDetails = ref(false);
const selectedEntry = ref<TimetableEntry | null>(null);

function handleCellClick({ entry }: { entry?: TimetableEntry }) {
  if (entry) {
    selectedEntry.value = entry;
    showDetails.value = true;
  }
}

function formatTime(time: string) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const formattedHours = h % 12 || 12;
  return `${formattedHours}:${m.toString().padStart(2, '0')} ${ampm}`;
}

</script>

<style scoped></style>
